import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';

const FORM_ACTION = 'https://readdy.ai/api/form/db8apmokk00l6eltri0g';

const POSITIONING = [
  {
    icon: 'ri-compass-3-line',
    title: '只做信息与推荐',
    text: '我们把重心放在作品信息的整理与推荐上，帮助成年观众在最短时间内判断一部作品是否值得一看。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '面向成年用户',
    text: '本站内容面向成年用户，未满十八周岁请勿浏览。所有作品均为成人向题材的信息展示。',
  },
  {
    icon: 'ri-database-2-line',
    title: '演示数据说明',
    text: '站内片名、导演、演员与简介均为演示数据，不涉及真实作品信息，仅用于产品效果展示。',
  },
];

const NOTES = [
  '本站不提供任何在线播放、下载或缓存功能，仅展示影视作品的基础信息与观看引导。',
  '站内“正版流媒体 / 线下影院 / 官方平台”等入口均为示意，不指向任何真实第三方链接。',
  '收藏功能仅保存在你的浏览器本地，不会上传、不会共享，清除浏览器数据后收藏将一并清除。',
  '请遵守你所在地区的法律法规，合理安排观影时间，支持正版影视内容。',
];

export default function About() {
  useSeo(
    '关于我们 - 十八禁影视大全',
    '了解十八禁影视大全的网站定位、内容原则与联系方式，并可通过反馈表单与我们取得联系。',
  );

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      return;
    }

    setStatus('loading');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'contact_alt') return;
      if (typeof value === 'string' && value.trim() !== '') payload.append(key, value);
    });

    try {
      const response = await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText ?? '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="w-full pt-16 md:pt-20">
      <div className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-10 md:pb-14 bg-background-100/50 border-b border-background-200/60">
        <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4 flex-wrap">
          <Link to="/" className="hover:text-primary-400 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">关于我们</span>
        </nav>
        <p className="font-label text-xs text-accent-400 tracking-[0.28em] mb-3">ABOUT US</p>
        <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 font-bold">关于十八禁影视大全</h1>
        <p className="mt-4 text-sm md:text-base text-foreground-700 max-w-3xl leading-relaxed">
          十八禁影视大全是一个聚焦成人向影视作品的信息与推荐平台，收录最新的十八禁电影与电视剧，
          以清晰的分区、专业的多维筛选与用心的专题策划，帮助成年观众更快找到想看的作品。
        </p>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        {/* 网站定位 */}
        <section>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950 font-bold flex items-center gap-3 mb-6">
            <span className="w-1 h-5 bg-primary-500 rounded-full" />
            网站定位
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {POSITIONING.map((item) => (
              <div key={item.title} className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6">
                <span className="w-11 h-11 rounded-md bg-secondary-100 flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-xl text-accent-400`} />
                </span>
                <h3 className="font-heading text-base text-foreground-950 font-bold">{item.title}</h3>
                <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 使用须知 + 版权 */}
        <section className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6">
            <h2 className="font-heading text-lg md:text-xl text-foreground-950 font-bold flex items-center gap-3 mb-4">
              <span className="w-1 h-5 bg-accent-500 rounded-full" />
              使用须知
            </h2>
            <ul className="space-y-3">
              {NOTES.map((note) => (
                <li key={note} className="flex gap-3 text-sm text-foreground-700 leading-relaxed">
                  <i className="ri-checkbox-circle-line text-accent-400 mt-0.5 shrink-0" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6">
            <h2 className="font-heading text-lg md:text-xl text-foreground-950 font-bold flex items-center gap-3 mb-4">
              <span className="w-1 h-5 bg-primary-500 rounded-full" />
              版权声明
            </h2>
            <p className="text-sm text-foreground-700 leading-relaxed">
              本站为影视信息展示与推荐平台，不存储、不提供任何影视资源。站内展示的片名、简介、评分等均为演示数据，
              与任何真实影视作品无关。如本站内容无意中涉及第三方权益，请通过下方反馈入口与我们联系，
              我们将及时核查并处理。
            </p>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
              本站原创的页面结构、专题策划与文案整理内容，版权归本站所有；未经许可，请勿用于商业用途。
            </p>
            <div className="mt-5 flex flex-wrap gap-3 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-2 bg-background-50 border border-background-200 rounded-md px-3 py-2">
                <i className="ri-mail-line text-accent-400" />
                反馈邮箱：contact@shibajin.example
              </span>
              <span className="inline-flex items-center gap-2 bg-background-50 border border-background-200 rounded-md px-3 py-2">
                <i className="ri-time-line text-accent-400" />
                我们通常在 1-2 个工作日内回复
              </span>
            </div>
          </div>
        </section>

        {/* 联系方式表单 */}
        <section className="mt-12">
          <div className="bg-primary-500/10 border border-primary-500/25 rounded-lg p-5 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 font-bold flex items-center gap-3 mb-3">
              <span className="w-1 h-5 bg-primary-500 rounded-full" />
              联系我们
            </h2>
            <p className="text-sm text-foreground-700 leading-relaxed max-w-2xl">
              欢迎通过下方表单给我们留言，无论是内容建议、作品推荐还是版权相关反馈，我们都会认真阅读。
            </p>

            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="aux-verify-note"
              />

              <div>
                <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-2">
                  称呼
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder="怎么称呼你"
                  className="w-full bg-background-50 border border-background-200 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-2">
                  邮箱
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收我们的回复"
                  className="w-full bg-background-50 border border-background-200 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="contact-topic" className="block text-xs text-foreground-600 mb-2">
                  反馈类型
                </label>
                <select
                  id="contact-topic"
                  name="topic"
                  defaultValue="内容建议"
                  className="w-full bg-background-50 border border-background-200 rounded-md px-4 py-3 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors cursor-pointer"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="作品推荐">作品推荐</option>
                  <option value="功能反馈">功能反馈</option>
                  <option value="版权相关">版权相关</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-2">
                  留言内容（最多 500 字）
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述你的想法或遇到的问题"
                  className="w-full bg-background-50 border border-background-200 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                />
              </div>

              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4">
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-foreground-950 text-sm rounded-md px-7 py-3 whitespace-nowrap cursor-pointer transition-colors"
                >
                  {status === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      提交反馈
                    </>
                  )}
                </button>

                {status === 'success' && (
                  <p className="flex items-center gap-2 text-sm text-accent-400 animate-fade-in">
                    <i className="ri-checkbox-circle-fill" />
                    提交成功，感谢你的反馈，我们会尽快回复！
                  </p>
                )}
                {status === 'error' && (
                  <p className="flex items-center gap-2 text-sm text-primary-300 animate-fade-in">
                    <i className="ri-error-warning-fill" />
                    {formError || '提交失败，请稍后重试。'}
                  </p>
                )}
              </div>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}